import { Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ToastAction {
    label: string;
    callback: () => void;
    class?: string;
}

export interface ToastInfo {
    header?: string;
    body: string | TemplateRef<any>;
    class?: string;
    delay?: number;
    autohide?: boolean;
    actions?: ToastAction[];
}

@Injectable({ providedIn: 'root' })
export class ToastService {

    private toastsSubject = new BehaviorSubject<ToastInfo[]>([]);
    toasts$ = this.toastsSubject.asObservable();

    private get toasts(): ToastInfo[] {
        return this.toastsSubject.value;
    }

    show(toast: Partial<ToastInfo> & { body: string | TemplateRef<any> }) {
        const newToast: ToastInfo = {
            header: toast.header,
            body: toast.body,
            class: toast.class,
            delay: toast.delay ?? 5000,
            autohide: toast.autohide ?? true,
            actions: toast.actions ?? []
        };

        this.toastsSubject.next([...this.toasts, newToast]);
    }

    remove(toast: ToastInfo) {
        this.toastsSubject.next(this.toasts.filter(t => t !== toast));
    }

    success(
        body: string | TemplateRef<any>,
        header = 'Success',
        actions?: ToastAction[]
    ) {
        this.show({
            body,
            header,
            class: 'bg-success text-white border border-success',
            delay: 4000,
            actions
        });
    }

    error(
        body: string | TemplateRef<any>,
        header = 'Error',
        actions?: ToastAction[]
    ) {
        this.show({
            body,
            header,
            class: 'bg-danger text-white border border-danger',
            delay: 7000,
            actions
        });
    }

    info(
        body: string | TemplateRef<any>,
        header = 'Info',
        actions?: ToastAction[]
    ) {
        this.show({
            body,
            header,
            class: 'bg-info text-white border border-info',
            delay: 5000,
            actions
        });
    }

    warning(
        body: string | TemplateRef<any>,
        header = 'Warning',
        actions?: ToastAction[]
    ) {
        this.show({
            body,
            header,
            class: 'bg-warning text-dark border border-warning',
            delay: 6000,
            actions
        });
    }

    custom(
        body: string | TemplateRef<any>,
        options: {
            header?: string;
            class?: string;
            delay?: number;
            autohide?: boolean;
            actions?: ToastAction[];
        } = {}
    ) {
        this.show({
            body,
            ...options
        });
    }
}